import React from 'react';
import ModalHeader from '../molecules/ModalHeader';
import ModalFooter from '../molecules/ModalFooter';

/**
 * Step1AdditionalNight - Paso 1: Selección de noche adicional
 *
 * Props:
 * - product: Objeto del producto principal
 * - additionalProduct: Objeto del producto adicional (opcional)
 * - selectedVariants: Objeto con variantes seleccionadas
 * - totalPrice: Objeto { usd, cop } con precio total base
 * - includeAdditionalNight: Boolean indicando si incluye noche adicional
 * - onChange: Función callback (value) => void para cambiar includeAdditionalNight
 * - onContinue: Función callback para continuar
 * - className: Clases CSS adicionales (opcional)
 */
function Step1AdditionalNight({
  product,
  additionalProduct,
  selectedVariants,
  totalPrice,
  includeAdditionalNight = false,
  onChange,
  onContinue,
  className = ''
}) {
  // Use dynamic additional product price
  const additionalNightPrice = additionalProduct ? {
    usd: additionalProduct.priceUsd,
    cop: additionalProduct.priceCop
  } : { usd: 0, cop: 0 };

  // Calculate total with additional night
  const finalTotal = includeAdditionalNight
    ? {
        usd: totalPrice.usd + additionalNightPrice.usd,
        cop: totalPrice.cop + additionalNightPrice.cop
      }
    : totalPrice;

  // Format prices
  const formatUSD = (amount) => `$${amount.toLocaleString('en-US')}USD`;
  const formatCOP = (amount) => `$${amount.toLocaleString('es-CO')}`;

  // Get hotel name from selected variants
  const hotelName = selectedVariants.hotel || 'Hotel';
  const roomType = selectedVariants.room || selectedVariants.habitacion || 'Habitación';

  return (
    <div className={`bg-white d-flex flex-column justify-content-around align-items-center gap-4 w-100 p-4 mx-auto ${className}`}>
      <ModalHeader
        title="Noche Adicional"
        currentStep={1}
      />
      {additionalProduct && (
        <div className="d-flex align-items-center justify-content-between px-4 py-3 bg-light rounded w-100">
          <div className="d-flex align-items-center justify-content-between gap-3 w-100">
            <div className="d-flex justify-content-center align-items-center gap-3">
              <span className="fw-medium text-dark">{roomType}</span>
              <div className="d-inline-flex">
                <p className="mb-0 text-primary fw-medium">
                  {formatCOP(additionalNightPrice.cop)}/
                </p>
                <p className="mb-0 text-success">
                  {formatUSD(additionalNightPrice.usd)}
                </p>
              </div>
            </div>
            <input
              type="checkbox"
              checked={includeAdditionalNight}
              onChange={(e) => onChange(e.target.checked)}
              className="form-check-input"
              style={{width: '1.25rem', height: '1.25rem'}}
            />
          </div>
        </div>
      )}
      <div className="rounded p-4 w-100">
        {/* Price Breakdown */}
        <div className="d-flex flex-column gap-3">
          <div className="d-flex align-items-center gap-1 pb-2 border-bottom text-muted">
            <span>Subtotal: </span>
            <span>{formatCOP(totalPrice.cop)}</span>
          </div>

          <div className="d-flex flex-column align-items-start justify-content-center gap-1 pt-4 pb-4">
            <div className="d-flex align-items-center gap-2 text-muted">
              <span>{hotelName} {roomType}:</span>
              <span>{formatCOP(totalPrice.cop)}</span>
            </div>
            {includeAdditionalNight && (
              <div className="d-flex justify-content-start align-items-center gap-2 text-muted">
                <span>Noche Adicional {roomType}:</span>
                <span>{formatCOP(additionalNightPrice.cop)}</span>
              </div>
            )}
          </div>

          {/* Per Person Breakdown - Only for Twin/Triple (maxPersons >= 2) */}
          {product.maxPersons >= 2 && (
            <div className="d-flex flex-column gap-3 mt-2 bg-light rounded p-3">
              <div className="fw-medium text-dark border-bottom pb-2">
                Desglose por Persona
              </div>
              <div className="d-flex flex-column gap-2">
                {Array.from({ length: product.maxPersons }, (_, index) => {
                  const personNumber = index + 1;
                  const unitPriceCop = product.priceCop;
                  const additionalUnitPriceCop = additionalProduct ? additionalProduct.priceCop / product.maxPersons : 0;
                  const totalPerPersonCop = includeAdditionalNight
                    ? unitPriceCop + additionalUnitPriceCop
                    : unitPriceCop;

                  return (
                    <div key={personNumber} className="d-flex flex-column gap-1">
                      <div className="d-flex justify-content-between align-items-center text-muted small">
                        <span>Pasajero {personNumber}:</span>
                        <span>{formatCOP(totalPerPersonCop)}</span>
                      </div>
                      {includeAdditionalNight && (
                        <div className="text-muted small text-end">
                          Incluye noche adicional
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Total */}
        <div className="d-flex justify-content-center w-100">
          <div className="bg-primary text-white text-center px-4 py-2 rounded">
            <span className="fs-5 fw-semibold">
              Total: {formatCOP(finalTotal.cop)}
            </span>
          </div>
        </div>
      </div>

      <ModalFooter
        onContinue={onContinue}
        continueText="Continuar con el Pedido"
        showBack={false}
      />
    </div>
  );
}

export default Step1AdditionalNight;
